import { useState } from "react";
import type { DfyBuildRunDto, Locale } from "../../shared/dto";
import {
  DFY_KNOWN_STEPS,
  dfyCatalogSteps,
  dfyStepLabel,
  dfyStepLabelKey,
  isDfyRunStale,
  mergeDfySteps
} from "../../shared/dfy-catalog";
import { buildStepKey } from "../../shared/staff-lifecycle";
import { BuildProgress } from "../BuildProgress";
import { formatDate, makeSafeT } from "./core";
import type { StaffPaneProps } from "./console-types";
import type { StaffRun } from "./model";
import "./dfy-build.css";
// Live CRM lookup by the client's e-mail (sales CRM; optional integration).
// Production builds start automatically since 2026-07-30. These controls are
// durable, server-backed intervention and retry tools for the partner.
export const DFY_GATES = ["audit", "salespage", "emails", "webinar"] as const;
export type DfyConsoleGate = (typeof DFY_GATES)[number];
export type DfyGateView =
  | { state: "idle"; run: null; note: null }
  | { state: "running" | "stale" | "failed" | "done"; run: DfyBuildRunDto | null; note: string | null };

/** Console gate -> the gate key the runner and the D1 rows use. */
export function runGateKey(gate: DfyConsoleGate): string {
  return gate === "audit" ? "strategy" : gate;
}

// The step catalogs live in `shared/dfy-catalog.ts` since F13. They used to be
// hand-typed here and had drifted: the e-mail gate listed 10 steps while the
// builder reported 20, so "step 7 of 10" was simply wrong. The console now
// consumes the same versioned truth the estimator measures against.
export const GATE_STEPS: Record<DfyConsoleGate, readonly string[]> = {
  audit: dfyCatalogSteps("strategy"),
  salespage: dfyCatalogSteps("salespage"),
  emails: dfyCatalogSteps("emails"),
  webinar: dfyCatalogSteps("webinar")
};

export const KNOWN_STEPS = DFY_KNOWN_STEPS;

export { dfyStepLabel, isDfyRunStale, mergeDfySteps };

export function dfyStrategyStepLabelKey(step: string): string | null {
  return dfyStepLabelKey(step);
}

export function dfyGateView(
  gate: DfyConsoleGate,
  runs: DfyBuildRunDto[],
  deliverables: Array<{ kind?: string; status?: string }>,
  now = Date.now()
): DfyGateView {
  const runGate = gate === "audit" ? "strategy" : gate;
  const deliverableKind = gate === "audit" ? "plan" : gate;
  const run = runs.find((candidate) => candidate.gateKey === runGate) ?? null;
  // State truth law: a live rebuild outranks an existing deliverable - the
  // partner must SEE that a new version is being produced right now.
  if (run?.status === "running" && !isDfyRunStale(run, now)) {
    return { state: "running", run, note: run.note };
  }
  const targets = deliverables.filter((candidate) => candidate.kind === deliverableKind);
  // A completed output survives a later process failure. A draft alone
  // proves existence, but cannot clear a failed or silent production run.
  if (targets.some((candidate) => candidate.status !== "draft") || run?.status === "done") {
    return { state: "done", run, note: run?.note ?? null };
  }
  if (run?.status === "failed") return { state: "failed", run, note: run.note };
  if (run?.status === "running") {
    return { state: "stale", run, note: run.note };
  }
  if (targets.length) return { state: "done", run, note: run?.note ?? null };
  return { state: "idle", run: null, note: null };
}

/**
 * 🔴 F138 kolečko 3, fault 6: a DFY row states what the material IS.
 *
 * The list used to carry a gate name, a "Spustit výrobu" button and, once a
 * material existed, "Otevřít podklady" - and nothing else. So the row about a
 * FINISHED, approved, published sales page looked exactly like the row about a
 * webinar nobody had ever built, and the note above the list ("výroba se
 * spouští automaticky, tlačítka jen pro ruční zásah") told the partner that
 * the only two things the row offered were the things they should not need.
 *
 * The state comes from the DELIVERABLES the detail already carries - the same
 * rows the material card reads - so the overview cannot disagree with the step.
 * `published` beats `status` where they differ: published is what the CLIENT
 * can see, and that is the question a partner opens this list with.
 */
export interface DfyGateMaterial {
  state: "none" | "draft" | "review" | "approved" | "published";
  version: number | null;
  at: string | null;
}

export function dfyGateMaterial(
  gate: DfyConsoleGate,
  deliverables: ReadonlyArray<Record<string, unknown>>
): DfyGateMaterial {
  const kind = gate === "audit" ? "plan" : gate;
  const rows = deliverables.filter((row) => row.kind === kind);
  if (!rows.length) return { state: "none", version: null, at: null };
  const newest = rows.reduce((best, row) =>
    Number(row.version ?? 0) > Number(best.version ?? 0) ? row : best);
  const version = Number.isFinite(Number(newest.version)) ? Number(newest.version) : null;
  const published = newest.published === true || Number(newest.published) === 1;
  const status = String(newest.status ?? "");
  const stamp = (value: unknown) => (typeof value === "string" && value ? value : null);
  if (status === "approved") return { state: "approved", version, at: stamp(newest.approvedAt) };
  if (published) return { state: "published", version, at: stamp(newest.publishedAt) };
  if (status === "review") return { state: "review", version, at: stamp(newest.updatedAt) };
  return { state: "draft", version, at: stamp(newest.updatedAt) };
}

export function DfyBuildCard(props: StaffPaneProps) {
  const [busyGate, setBusyGate] = useState<string | null>(null);
  const [error, setError] = useState<"blocked" | "failed" | null>(null);
  const salesReady = props.detail.sections.some((s) => s.key === "sales" && (s.state === "working" || s.state === "done"));
  // The partner cannot read draft bodies, but must see that the target
  // already exists: a first-build request for it would only be skipped.
  const deliverables = [...(props.detail.deliverables ?? []),
    ...(props.detail.productionMaterials ?? []).map((row) => ({ ...row }))];
  const approved = (kind: string) => deliverables.some((d) => d.kind === kind && d.status === "approved");
  const gateEnabled = (gate: DfyConsoleGate) => salesReady && (gate === "audit" || approved("plan"));
  async function launch(gate: DfyConsoleGate) {
    setBusyGate(gate);
    setError(null);
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/dfy-build`, { method: "POST", body: { gate } }, { refresh: false });
    setBusyGate(null);
    if (response.status === 409) { setError("blocked"); return; }
    if (!response.ok) { setError("failed"); return; }
    await props.refresh();
  }
  return (
    <article className="staff-card dfy-build-card">
      <h3>{props.t("staffConsole.dfyBuild.title")}</h3>
      <p className="staff-empty dfy-build-hint">{props.t(salesReady ? "staffConsole.dfyBuild.hint" : "staffConsole.dfyBuild.notReady")}</p>
      <div className="dfy-gate-list">
        {DFY_GATES.map((gate) => {
          const view = dfyGateView(gate, props.detail.dfyRuns ?? [], deliverables);
          const material = dfyGateMaterial(gate, deliverables);
          const hasTarget = material.state !== "none";
          const enabled = gateEnabled(gate) && !hasTarget && view.state !== "running" && view.state !== "done";
          return (
            <section className={`dfy-gate-row is-${view.state}`} key={gate} data-dfy-gate={gate}>
              <div className="dfy-gate-row-head">
                <span className="dfy-gate-row-title">
                  <strong>{props.t(`staffConsole.dfyBuild.gates.${gate}`)}</strong>
                  {/* What the material IS, from the deliverables the detail
                      already carries. A row with no material says so rather
                      than saying nothing at all. */}
                  <span className="dfy-gate-material" data-dfy-material={material.state}>
                    {material.version !== null && (
                      <>{props.t("staffConsole.dfyBuild.materialVersion").replace("{version}", String(material.version))} · </>
                    )}
                    {props.t(`staffConsole.dfyBuild.materialState.${material.state}`)}
                    {material.at && ` · ${formatDate(material.at, props.locale)}`}
                  </span>
                </span>
                {/* 🔴 The launch is a SECONDARY act and now looks like one, and
                    it is drawn only while it can DO something.
                    The note above this list says the build starts by itself and
                    these buttons are for a hand intervention, and yet the
                    button was the loudest thing in the row - the only control a
                    partner could see, four times over, permanently disabled on
                    three of the four. A disabled control explains itself through
                    a `title`, which a touch screen cannot reach at all (P-18),
                    and at 390 the stacked head gave that dead control a line of
                    its own above the link that works. What the row cannot offer,
                    it does not draw: the state line beside the name and the card
                    hint above already say why. */}
                {enabled && (
                  <button
                    className="text-button dfy-gate-launch"
                    type="button"
                    disabled={busyGate !== null}
                    onClick={() => void launch(gate)}
                  >
                    {/* The row title already names the gate; the button says the
                        ACTION (audit finding: "Návrh prodejní strategie" sat
                        beside a button reading "Návrh prodejní strategie"). */}
                    {busyGate === gate
                      ? props.t("staffConsole.dfyBuild.launching")
                      : view.state === "failed" || view.state === "stale"
                        ? props.t("staffConsole.dfyBuild.retry")
                        : props.t("staffConsole.dfyBuild.launchGate")}
                  </button>
                )}
              </div>
              {view.state === "running" && view.run && (
                <DfyRunPanel gate={gate} locale={props.locale} run={view.run} t={props.t} />
              )}
              {view.state === "stale" && (
                <p className="message warning-message dfy-run-strip" role="alert">{props.t("staffConsole.dfyBuild.stale")}</p>
              )}
              {view.state === "failed" && (
                <p className="message error-message dfy-run-strip" role="alert">
                  {props.t("staffConsole.dfyBuild.failed")}{view.note ? ` ${view.note}` : ""}
                </p>
              )}
              {/* F138a: this used to open the "Výstupy" tab, which showed
                  every material of every step at once. The finished material
                  has ONE home - its own step - so the link goes there.
                  🔴 F138 kolečko 3: the link follows the MATERIAL, not the
                  run. A material that exists is openable even while a newer
                  version is being built, and `view.state` is about the run. */}
              {material.state !== "none" && props.onOpenStep && (
                <button
                  className="quiet-link dfy-run-done"
                  type="button"
                  onClick={() => props.onOpenStep?.(buildStepKey(gate))}
                >
                  {props.t("staffConsole.dfyBuild.openDeliverables")}
                </button>
              )}
            </section>
          );
        })}
      </div>
      {error === "blocked" && <p className="message error-message" role="alert">{props.t("staffConsole.dfyBuild.gateLocked")}</p>}
      {error === "failed" && <p className="message error-message" role="alert">{props.t("staffConsole.error")}</p>}
    </article>
  );
}

export function DfyRunPanel(props: {
  gate: DfyConsoleGate;
  locale: Locale;
  run: DfyBuildRunDto;
  t: (key: string) => string;
  now?: number;
  timeZone?: string;
}) {
  const now = props.now ?? Date.now();
  const elapsed = Math.max(0, Math.floor((now - Date.parse(props.run.startedAt)) / 60_000));
  const lastActivity = Math.max(0, Math.floor((now - Date.parse(props.run.updatedAt)) / 60_000));
  // An estimate that would land in the past is not an estimate. The server
  // already refuses to build one; this is the last fence.
  const eta = props.run.eta && Date.parse(props.run.eta.etaAt) > now ? props.run.eta : null;
  return (
    <div className="dfy-run-panel">
      <div className="dfy-run-meta">
        <span>{props.t("staffConsole.dfyBuild.elapsed").replace("{minutes}", String(elapsed))}</span>
        <span>{props.t("staffConsole.dfyBuild.lastActivity").replace("{minutes}", String(lastActivity))}</span>
      </div>
      {/* The flat step grid and the separate estimate line became one timeline
          (Pavel 2026-08-05): a partner staring at twenty equal-weight step names
          could not tell what had already cost time from what was still ahead. */}
      <BuildProgress
        gateKey={runGateKey(props.gate)}
        steps={props.run.steps}
        stepManifest={props.run.stepManifest ?? null}
        startedAt={props.run.startedAt}
        timings={props.run.timings ?? null}
        eta={eta}
        locale={props.locale}
        t={props.t}
        density="panel"
        showReadyAt
        timeZone={props.timeZone}
        now={props.now}
      />
    </div>
  );
}
